import { dedupeMessagesById } from "@/api/dedupeMessagesById";
import { useToolConfirmationNotifications } from "@/hooks/useToolConfirmationNotifications";
import {
  FlowSession,
  Message,
  MessageResponse,
  MessagesResponse,
  PostMessageParams,
  ToolCall,
} from "@cohesive/api-client";
import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { cloneDeep } from "lodash";
import { useCallback } from "react";
import { orgAPI } from "./api";
import { sessionWritesInFlight, settleSessionWrites } from "./sessionWrites";

export function useFlowMessage(
  flowSessionId: string,
  messageId: string,
  options?: Partial<UseQueryOptions<MessageResponse, AxiosError, Message>>,
) {
  return useQuery({
    queryKey: ["flowMessage", flowSessionId, messageId],
    queryFn: async () => {
      if (!flowSessionId) {
        throw new Error("Flow session ID is required");
      }
      if (!messageId) {
        throw new Error("Message ID is required");
      }

      return await orgAPI.flowMessages.get(flowSessionId, messageId);
    },
    select: (data) => data.data.flow_message,
    enabled: !!flowSessionId && !!messageId,
    ...options,
  });
}

export function useFlowMessages(
  flowSessionId: string,
  sinceTimestamp?: string,
  options?: Partial<
    UseInfiniteQueryOptions<
      MessagesResponse,
      Error,
      Message[],
      MessagesResponse,
      unknown[],
      string | undefined
    >
  >,
) {
  return useInfiniteQuery({
    queryKey: ["flowMessages", flowSessionId],
    initialPageParam: sinceTimestamp,
    queryFn: async ({ pageParam }) => {
      if (!flowSessionId) {
        console.error("Flow session ID is required");
        throw new Error("Flow session ID is required");
      }

      const options =
        (pageParam ?? sinceTimestamp)
          ? { before_ts: pageParam ?? sinceTimestamp }
          : {};
      return await orgAPI.flowMessages.list(flowSessionId, options);
    },
    getNextPageParam: (page) => {
      if (page.pagination?.has_more) {
        return page.pagination?.next_ts;
      }

      return undefined;
    },
    getPreviousPageParam: () => {
      return undefined;
    },
    select: (pages) => {
      const messages = pages?.pages
        ?.flatMap((page) => page.data.flow_messages)
        .reverse();
      return messages && dedupeMessagesById(messages);
    },
    ...options,
  });
}

// Keyed by the mutation's variables, which onMutate and mutationFn share.
const writesAtSend = new WeakMap<object, Promise<boolean>[]>();

export function usePostFlowMessage(
  options?: UseMutationOptions<
    Message | undefined,
    AxiosError,
    PostMessageParams,
    {
      previousMessages?: InfiniteData<MessagesResponse>;
      optimisticMessage?: Message;
      hadPendingToolConfirmations?: boolean;
    }
  >,
) {
  const updateMessageCache = useMessageCacheOperations();
  const queryClient = useQueryClient();
  const { deleteForSession } = useToolConfirmationNotifications();

  return useMutation<
    Message | undefined,
    AxiosError,
    PostMessageParams & { flowSessionId: string },
    {
      previousMessages?: InfiniteData<MessagesResponse>;
      optimisticMessage?: Message;
      hadPendingToolConfirmations?: boolean;
    }
  >({
    // Spread caller options FIRST so the hook's own handlers (below) always run
    // — a caller's onSuccess/onError would otherwise replace ours and drop the
    // notification cleanup / rollback. We compose the caller's callbacks in.
    ...options,
    mutationFn: async (variables) => {
      const {
        flowSessionId,
        id,
        content,
        file_record_ids,
        referenced_entities,
        reasoning_effort,
        model,
        harness_reasoning_effort,
        permission_mode,
        addendum,
      } = variables;
      // A pick written to the session (agent, model, tier) lands before the turn it shapes.
      await settleSessionWrites(flowSessionId, writesAtSend.get(variables));
      const response = await orgAPI.flowMessages.create(flowSessionId, {
        id,
        content,
        file_record_ids,
        referenced_entities,
        reasoning_effort,
        model,
        harness_reasoning_effort,
        permission_mode,
        addendum,
      });
      return response.data.flow_message;
    },
    onMutate: async (variables) => {
      // Before the first await: the writes in flight when Send was pressed, none settled unseen.
      writesAtSend.set(
        variables,
        sessionWritesInFlight(variables.flowSessionId),
      );
      const {
        id,
        flowSessionId,
        content,
        file_record_ids,
        referenced_entities,
      } = variables;
      // Compose the caller's onMutate too (options is spread first, so our
      // handlers are defined here); merge its context so our onError/onSuccess
      // still see previousMessages/optimisticMessage.
      const callerContext = await options?.onMutate?.(variables);

      await queryClient.cancelQueries({
        queryKey: ["flowMessages", flowSessionId],
      });

      const previousMessages = queryClient.getQueryData<
        InfiniteData<MessagesResponse>
      >(["flowMessages", flowSessionId]);

      // Capture whether there were pending confirmations *before* the send — a
      // racing flow_message.updated websocket event can clear them before
      // onSuccess, so reading it there could wrongly skip the notification cleanup.
      const hadPendingToolConfirmations =
        flowSessionHasPendingToolConfirmations(queryClient, flowSessionId);

      const optimisticMessage: Message = {
        id,
        user_id: "",
        organization_id: "",
        flow_session_id: flowSessionId,
        status: "pending",
        origin: "user",
        content: content || "",
        sent_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        file_record_ids,
        referenced_entities,
      };

      updateMessageCache(flowSessionId, {
        type: "upsert",
        message: optimisticMessage,
      });

      return {
        ...callerContext,
        previousMessages,
        optimisticMessage,
        hadPendingToolConfirmations,
      };
    },
    onSuccess: (data, variables, context) => {
      const { flowSessionId } = variables;
      // Sending a message auto-rejects any still-pending tool confirmations in
      // this chat, so their bell notifications are now stale — clear them. Gate on
      // the pre-send snapshot captured in onMutate (not a fresh read here): a
      // websocket flow_message.updated can clear the statuses before onSuccess,
      // which would wrongly skip cleanup — but it still avoids the retry loop for
      // ordinary sends that had no pending calls.
      if (context?.hadPendingToolConfirmations) {
        deleteForSession(flowSessionId);
      }

      // A message carrying a model moves the session's model for this turn and every one
      // after it, so the cached session has to agree — nothing else refetches it, and the
      // composer seeds its picker from that field.
      if (variables.model) {
        queryClient.setQueryData<FlowSession>(
          ["flowSession", flowSessionId],
          (prev) => (prev ? { ...prev, model: variables.model } : prev),
        );
      }

      // Same for the permission mode — the switch sticks on the session.
      if (variables.permission_mode) {
        queryClient.setQueryData<FlowSession>(
          ["flowSession", flowSessionId],
          (prev) =>
            prev
              ? { ...prev, permission_mode: variables.permission_mode! }
              : prev,
        );
      }

      if (data && context?.optimisticMessage) {
        queryClient.setQueryData<InfiniteData<MessagesResponse>>(
          ["flowMessages", flowSessionId],
          (prev) => {
            if (prev) {
              return {
                pages: prev.pages.map((page) => ({
                  ...page,
                  data: {
                    flow_messages: page.data.flow_messages.map((message) =>
                      message.id === context.optimisticMessage?.id
                        ? data
                        : message,
                    ),
                  },
                })),
                pageParams: prev.pageParams,
              };
            }

            return prev;
          },
        );
      }

      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      const { flowSessionId } = variables;
      if (context?.previousMessages) {
        queryClient.setQueryData<InfiniteData<MessagesResponse>>(
          ["flowMessages", flowSessionId],
          context.previousMessages,
        );
      }

      options?.onError?.(error, variables, context);
    },
  });
}

/**
 * Whether the flow session currently has any tool call awaiting user approval,
 * read from the flowMessages cache. Gates the message-send notification cleanup:
 * only when there were pending confirmations to auto-reject do we look up and
 * delete the (now stale) $toolConfirmation bell item. Returns false when the
 * messages aren't cached — no known pending, so skip the lookup.
 */
function flowSessionHasPendingToolConfirmations(
  queryClient: QueryClient,
  flowSessionId: string,
): boolean {
  const data = queryClient.getQueryData<InfiniteData<MessagesResponse>>([
    "flowMessages",
    flowSessionId,
  ]);
  if (!data) return false;

  return data.pages.some((page) =>
    page.data.flow_messages.some((message: Message) =>
      message.tool_calls?.some(
        (toolCall: ToolCall) => toolCall.user_confirmation_status === "pending",
      ),
    ),
  );
}

type MessageCacheOperation =
  | { type: "upsert"; message: Message }
  | { type: "upsert_content_delta"; message: Message }
  | { type: "mark_streaming_complete"; messageId: string };

export function useMessageCacheOperations() {
  const queryClient = useQueryClient();

  return useCallback(
    (flowSessionId: string, operation: MessageCacheOperation) => {
      queryClient.setQueryData<InfiniteData<MessagesResponse>>(
        ["flowMessages", flowSessionId],
        (oldData) => {
          const newData = oldData
            ? cloneDeep(oldData)
            : {
                pages: [],
                pageParams: [],
              };

          switch (operation.type) {
            case "upsert": {
              // Search through all pages to find and replace or insert the message
              let found = false;
              for (const page of newData.pages) {
                const index = page.data.flow_messages.findIndex(
                  (msg) => msg.id === operation.message.id,
                );
                if (index !== -1) {
                  page.data.flow_messages[index] = operation.message;
                  found = true;
                  break;
                }
              }

              // If not found, insert at the beginning of the first page
              if (!found) {
                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    operation.message,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: {
                      flow_messages: [operation.message],
                    },
                  });
                }
              }
              break;
            }

            case "upsert_content_delta": {
              let found = false;
              for (const page of newData.pages) {
                const existingMessage = page.data.flow_messages.find(
                  (msg) => msg.id === operation.message.id,
                );
                if (existingMessage) {
                  existingMessage.content += operation.message.content;
                  existingMessage.updated_at = new Date().toISOString();
                  // Mark message as actively streaming
                  (existingMessage as any).isStreaming = true;
                  found = true;
                  break;
                }
              }

              // If not found, insert as new message at the beginning of the first page
              if (!found) {
                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    operation.message,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: {
                      flow_messages: [operation.message],
                    },
                  });
                }
              }
              break;
            }

            case "mark_streaming_complete": {
              for (const page of newData.pages) {
                const message = page.data.flow_messages.find(
                  (msg) => msg.id === operation.messageId,
                );
                if (message) {
                  (message as any).isStreaming = false;
                  break;
                }
              }
              break;
            }
          }

          return newData;
        },
      );
    },
    [queryClient],
  );
}
